import { gql } from "lib/apolloClient";
import {
  organizationPresetFragment,
  organizationFragment,
  academicYearFragment,
  progressReportBaseTemplateFragment,
} from "./ToolingFragments";

export const updateBaseProgressReportTemplateMutation = gql`
  mutation updateBaseProgressReportTemplate($id: ID!, $body: JSON) {
    documentation {
      updateProgressReportBaseTemplate(input: { id: $id, body: $body }) {
        ...baseTemplateItemDetails
      }
    }
  }
  ${progressReportBaseTemplateFragment.baseTemplateItemDetails}
`;

export const bulkRemoveUsersFromMultiAccessIdentityMutation = gql`
  mutation bulkRemoveUsersFromMultiAccessIdentity(
    $input: BulkRemoveUsersFromMultiAccessIdentityInput
  ) {
    platform {
      bulkRemoveUsersFromMultiAccessIdentity(input: $input) {
        isSuccess
        updatedIdentityUserConnectionCount
      }
    }
  }
`;

export const createMultiAccessIdentityMutation = gql`
  mutation createMultiAccessIdentity($input: CreateMultiAccessIdentityInput) {
    platform {
      createMultiAccessIdentity(input: $input) {
        id
      }
    }
  }
`;

export const editMultiAccessIdentityMutation = gql`
  mutation editMultiAccessIdentity($input: EditMultiAccessIdentityInput) {
    platform {
      editMultiAccessIdentity(input: $input) {
        id
      }
    }
  }
`;

export const removeUserFromMultiAccessIdentityMutation = gql`
  mutation removeUserFromMultiAccessIdentity(
    $input: RemoveUserFromMultiAccessIdentityInput
  ) {
    platform {
      removeUserFromMultiAccessIdentity(input: $input)
    }
  }
`;

export const addUsersToMultiAccessIdentityMutation = gql`
  mutation addUsersToMultiAccessIdentity(
    $input: AddUsersToMultiAccessIdentityInput
  ) {
    platform {
      addUsersToMultiAccessIdentity(input: $input) {
        isSuccess
      }
    }
  }
`;

export const internalToolsGenericMutation = gql`
  mutation internalToolsGenericMutation(
    $apiType: INTERNAL_TOOL_API_TYPE_ENUM!
    $params: JSON!
  ) {
    platform {
      internalToolsGenericMutation(
        input: { apiType: $apiType, params: $params }
      ) {
        isSuccess
        warning
        data
      }
    }
  }
`;

// Delete and Deprecate State configuration
export const toggleConfigurationStateMutation = gql`
  mutation toggleConfigurationState($input: ConfigurationStateToggleInput!) {
    platform {
      toggleConfigurationState(input: $input)
    }
  }
`;

// Add generic configuration
export const createGenericConfigurationMutation = gql`
  mutation createConfiguration($input: ConfigurationToolGenericInput!) {
    platform {
      createConfiguration(input: $input) {
        id
        configuration
        configurationGroup
        dependentEntities {
          id
          entityGroup
        }
        description
        valueType
        value
      }
    }
  }
`;

// Edit generic configuration
export const editGenericConfigurationMutation = gql`
  mutation editConfiguration($input: ConfigurationToolGenericInput!) {
    platform {
      editConfiguration(input: $input) {
        id
        configuration
      }
    }
  }
`;

// Upsert configuration view
export const upsertConfigurationViewMutation = gql`
  mutation upsertConfigurationView($input: UpsertConfigurationViewInput!) {
    platform {
      upsertConfigurationView(input: $input) {
        categoryKey
      }
    }
  }
`;

export const addConfigurationValuesMutationV1 = gql`
  mutation addConfigurationValues($input: [ConfigurationValueUpdateInput]!) {
    platform {
      setMultipleConfigurationValueForEntityGroups(input: $input)
    }
  }
`;

// Add configuration values by Region
export const addConfigurationValuesMutationV2 = gql`
  mutation addConfigurationValues(
    $input: [ConfigurationValueByRegionUpdateInput]!
  ) {
    platform {
      setConfiguartionValueWithRegionId(input: $input)
    }
  }
`;

export const createOrganizationV2Mutation = gql`
  mutation createOrganizationV2(
    $input: OrganizationCreateV2Input!
    $curriculumProgramFilters: CurriculumProgramFilters
  ) {
    platform {
      createOrganizationV2(input: $input) {
        isSuccess
        warning
        organization {
          ...organizationItem
        }
      }
    }
  }
  ${organizationFragment.organizationList}
`;

export const updateCurriculumProgramsMutation = gql`
  mutation updateOrganizationV2(
    $input: OrganizationUpdateV2Input!
    $curriculumProgramFilters: CurriculumProgramFilters
  ) {
    platform {
      updateOrganizationV2(input: $input) {
        isSuccess
        warning
        organization {
          ...organizationItem
        }
      }
    }
  }
  ${organizationFragment.organizationList}
`;

export const updateOrganizationBasicDetailsMutation = gql`
  mutation updateOrganizationV2(
    $id: ID!
    $name: String
    $code: String
    $addedAttachments: [AttachmentInput!]
    $removedAttachments: [ID!]
  ) {
    platform {
      updateOrganizationV2(
        input: {
          id: $id
          name: $name
          code: $code
          addedAttachments: $addedAttachments
          removedAttachments: $removedAttachments
        }
      ) {
        organization {
          ...organizationItem
        }
      }
    }
  }
  ${organizationFragment.organizationBasicDetails}
`;

export const createOrganizationPresetMutation = gql`
  mutation createOrganizationPreset(
    $name: String!
    $domains: [String!]
    $city: String
    $country: String
    $ibCode: String
  ) {
    organizationManagement {
      createOrganizationPreset(
        input: {
          name: $name
          domains: $domains
          city: $city
          country: $country
          ibCode: $ibCode
        }
      ) {
        id
      }
    }
  }
`;

export const updateOrganizationPresetMutation = gql`
  mutation updateOrganizationPreset(
    $id: ID!
    $name: String!
    $domains: [String!]
    $city: String
    $country: String
    $ibCode: String
  ) {
    organizationManagement {
      updateOrganizationPreset(
        input: {
          id: $id
          name: $name
          domains: $domains
          city: $city
          country: $country
          ibCode: $ibCode
        }
      ) {
        ...organizationItem
      }
    }
  }
  ${organizationPresetFragment.orgItem}
`;

export const deleteOrganizationPresetMutation = gql`
  mutation deleteOrganizationPreset($id: ID!) {
    organizationManagement {
      deleteOrganizationPreset(input: { id: $id })
    }
  }
`;

export const approveOrganizationMutation = gql`
  mutation approveSchool(
    $id: ID!
    $name: String!
    $domains: [String!]
    $city: String
    $country: String
    $ibCode: String
  ) {
    organizationManagement {
      approveSchool(
        input: {
          id: $id
          name: $name
          domains: $domains
          city: $city
          country: $country
          ibCode: $ibCode
        }
      ) {
        id
      }
    }
  }
`;

export const deleteApproveSchoolRequestMutation = gql`
  mutation deleteApproveSchoolRequest($id: ID!) {
    organizationManagement {
      deleteApproveSchoolRequest(input: { id: $id }) {
        warning
        isSuccess
      }
    }
  }
`;

export const deleteConfigurationValueMutation = gql`
  mutation deleteConfigurationValue($ids: [ID!]!, $isDeleted: Boolean) {
    platform {
      updateConfigurationValueById(input: { ids: $ids, isDeleted: $isDeleted })
    }
  }
`;

export const updateConfigurationEntityValueMutation = gql`
  mutation updateConfigurationEntityValue(
    $input: [ConfigurationValueUpdateByIdInputV2!]!
  ) {
    platform {
      updateConfigurationValueByIdV2(input: $input)
    }
  }
`;

export const approveUserMutation = gql`
  mutation approveUser($presetOrgId: ID!, $unauthenticatedUserId: ID!) {
    school {
      approveFirstUserWorkEmailRequest(
        presetOrgId: $presetOrgId
        unauthenticatedUserId: $unauthenticatedUserId
      )
    }
  }
`;

export const deleteApproveUserRequestMutation = gql`
  mutation deleteApproveUserRequest($id: ID!) {
    organizationManagement {
      deleteApproveUserRequest(input: { id: $id }) {
        warning
        isSuccess
      }
    }
  }
`;

// User Feed

//Edit Standard
export const editStandardInfoMutation = gql`
  mutation editStandardInfo($input: StandardInfo!) {
    planner {
      updateStandardInfo(input: $input) {
        id
        title
        description
        globalSubjects
        code
        curriculumName
        country
        state
        curriculumTypes
      }
    }
  }
`;

// Create or Edit Academic Year
// While Creating it will pass OrgId, While Editing it will pass Academic Year's id
export const updateOrganizationAcademicYearMutation = gql`
  mutation updateOrganization(
    $orgId: ID!
    $id: ID!
    $startDate: String!
    $endDate: String!
  ) {
    organizationManagement {
      manageOrganizationAcademicYear(
        input: {
          id: $orgId
          academicYear: {
            id: $id
            dates: { startDate: $startDate, endDate: $endDate }
          }
        }
      ) {
        warning
        response {
          ...academicYear
        }
      }
    }
  }
  ${academicYearFragment.academicYear}
`;

export const createOrganizationAcademicYearMutation = gql`
  mutation updateOrganization(
    $id: ID!
    $startDate: String!
    $endDate: String!
  ) {
    organizationManagement {
      manageOrganizationAcademicYear(
        input: {
          id: $id
          newAcademicYear: { startDate: $startDate, endDate: $endDate }
        }
      ) {
        warning
        response {
          ...academicYear
        }
      }
    }
  }
  ${academicYearFragment.academicYear}
`;

export const deleteOrganizationMutation = gql`
  mutation deleteOrganization($id: ID!) {
    platform {
      deleteOrganization(input: { id: $id }) {
        isSuccess
      }
    }
  }
`;

export const updateConfigurablePermissionMutation = gql`
  mutation updateConfigurablePermission(
    $id: ID!
    $permissions: [ConfigurablePermissionToggleInput!]!
  ) {
    platform {
      toggleOrganizationConfigurablePermission(
        input: { organizationId: $id, permissions: $permissions }
      )
    }
  }
`;

export const deleteStandardMutation = gql`
  mutation deleteStandard($id: ID!) {
    planner {
      deleteStandardBenchmarkSet(input: { id: $id }) {
        isSuccess
      }
    }
  }
`;

export const updateOrganizationPaidStatusMutation = gql`
  mutation updateOrganizationPaidStatus(
    $organizationId: ID!
    $isPaid: Boolean!
  ) {
    organizationManagement {
      updateOrganizationPaidStatus(
        input: { organizationId: $organizationId, isPaid: $isPaid }
      )
    }
  }
`;

export const validateInternalToolsModulePasswordMutation = gql`
  mutation validateInternalToolsModulePasswordMutation(
    $input: InternalToolsModulePasswordInput!
  ) {
    platform {
      validateInternalToolsModulePassword(input: $input)
    }
  }
`;

export const removeUserIdentityFromMultiAccessIdentityMutation = gql`
  mutation removeUserIdentityFromMultiAccessIdentity(
    $input: removeUserIdentityFromMultiAccessIdentityInput!
  ) {
    platform {
      removeUserIdentityFromMultiAccessIdentity(input: $input) {
        isSuccess
      }
    }
  }
`;

export const configurationMultiValueUpsertMutation = gql`
  mutation configurationMultiValueUpsert(
    $input: ConfigurationMultiValueUpsertInput!
  ) {
    platform {
      executeConfigurationMultiValueUpsert(input: $input)
    }
  }
`;

export const configurationCacheFlushMutation = gql`
  mutation configurationCacheFlush($input: ConfigurationCacheFlushInput!) {
    platform {
      flushConfigurationCache(input: $input)
    }
  }
`;

export const createOrganizationAccessRequestForUserMutation = gql`
  mutation createOrganizationRequestForUser(
    $input: AddUsersRequestForMultiAccessIdentityInput
  ) {
    platform {
      addRequestForUsersMultiAccessIdentity(input: $input) {
        isSuccess
        warning
        __typename
      }
      __typename
    }
  }
`;

export const createMultiAccessIdentityUserAccessRequestMutation = gql`
  mutation createMultiAccessIdentityUserAccessRequest(
    $input: AddRequestForMultiAccessIdentityUserAccessInput
  ) {
    platform {
      addRequestForMultiAccessIdentityUserAccess(input: $input) {
        isSuccess
        warning
        __typename
      }
      __typename
    }
  }
`;

export const editRequestForOrganizationAccessMutation = gql`
  mutation editRequestForOrganizationRequest(
    $input: EditRequestForMultiAccessIdentityInput
  ) {
    platform {
      editRequestForMultiAccessIdentity(input: $input) {
        isSuccess
        warning
        __typename
      }
      __typename
    }
  }
`;

export const editRequestForUserAccessMutation = gql`
  mutation editRequestForUserAccessRequest(
    $input: EditRequestForMultiAccessIdentityInput
  ) {
    platform {
      editRequestForMultiAccessIdentityUserAccess(input: $input) {
        isSuccess
        warning
        __typename
      }
      __typename
    }
  }
`;

export const editSchoolCloudStorageAccountMutation = gql`
  mutation updateOrganizationV2($input: OrganizationUpdateV2Input) {
    platform {
      updateOrganizationV2(input: $input) {
        organization {
          id
        }
      }
    }
  }
`;

export const createSurveyRulesetMutation = gql`
  mutation createSurveyRuleset($input: CreateSurveyRulesetInput!) {
    platform {
      createSurveyRuleset(input: $input) {
        isSuccess
        warning
        ruleset {
          id
          name
          rules {
            ruleId
            ruleType
            ruleValue
            operator
            ruleOrder
            afterGroupCondition
          }
        }
      }
    }
  }
`;

export const createCompleteSurveyMutation = gql`
  mutation createCompleteSurvey($input: CreateCompleteSurveyInput!) {
    platform {
      createCompleteSurvey(input: $input) {
        isSuccess
        warning
        survey {
          surveyId
          surveyTitle
          startDate
          endDate
          pageUrl
          description
          stages {
            stageId
            question
            questionType
            questionOptions
            stageOrder
            dependsOnStageId
            dependencyAnswer
          }
        }
      }
    }
  }
`;

export const deleteMultiAccessIdentityRequestMutation = gql`
  mutation deleteMultiAccessIdentityUserAccess(
    $input: EditRequestForMultiAccessIdentityInput!
  ) {
    platform {
      deleteRequestForMultiAccessIdentityUserAccess(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const createOrganizationAccountMutation = gql`
  mutation createOrganizationAccount(
    $input: OrganizationCreateV2Input!
    $curriculumProgramFilters: CurriculumProgramFilters
  ) {
    platform {
      createOrganizationV2(input: $input) {
        isSuccess
        warning
        organization {
          ...organizationItem
        }
      }
    }
  }
  ${organizationFragment.organizationList}
`;

export const updateAccountDetailsMutation = gql`
  mutation updateAccountDetails(
    $input: OrganizationUpdateV2Input!
    $curriculumProgramFilters: CurriculumProgramFilters
  ) {
    platform {
      updateOrganizationV2(input: $input) {
        isSuccess
        warning
        organization {
          ...organizationItem
        }
      }
    }
  }
  ${organizationFragment.organizationList}
`;

export const deleteCurriculumProgramMutation = gql`
  mutation updateAccountDetails($input: OrganizationUpdateV2Input!) {
    platform {
      updateOrganizationV2(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const bulkUpdateConfigurationMutation = gql`
  mutation setConfigurationValue($input: [ConfigurationValueUpdateInput!]!) {
    platform {
      setMultipleConfigurationValueForEntityGroups(input: $input)
    }
  }
`;

export const startOrgCopyPipelineMutation = gql`
  mutation startOrgCopyPipeline($input: StartOrgCopyPipelineInput!) {
    platform {
      startOrgCopyPipeline(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const bulkUploadByFileMutation = gql`
  mutation bulkUploadByFile(
    $url: String!
    $type: BULK_UPLOAD_TYPE_ENUM!
    $organizationId: ID
    $inputs: JSON
  ) {
    platform {
      bulkUploadByFile(
        input: {
          url: $url
          type: $type
          organizationId: $organizationId
          inputs: $inputs
        }
      ) {
        successful
        errorsJSON
      }
    }
  }
`;
